// 宇宙天気：NOAA SWPC の公開JSON（CORS: * のためブラウザから直接取得）
// - products/noaa-scales.json        … R（電波障害＝太陽フレア）/ S（放射線）/ G（磁気嵐）の現況
// - products/noaa-planetary-k-index.json … 3時間ごとの Kp 指数
// - json/goes/primary/xray-flares-latest.json … 直近のフレア
const BASE = 'https://services.swpc.noaa.gov';
const CACHE_KEY = 'ki_spacewx_v1';
const CACHE_MS = 30 * 60 * 1000;

export type SpaceWeatherLevel = 'quiet' | 'active' | 'storm';

export type SpaceWeather = {
  level: SpaceWeatherLevel;
  kp: number | null;           // 最新の Kp（0〜9）
  gScale: number;              // 磁気嵐 G0〜G5
  rScale: number;              // 電波障害 R0〜R5
  flareClass: string | null;   // 直近24時間のフレア最大クラス（例：M1.2）
  fetchedAt: number;
};

async function getJson(path: string): Promise<any> {
  const r = await fetch(BASE + path, { cache: 'no-store' });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

function toNum(v: any): number {
  const n = parseInt(String(v ?? '0'), 10);
  return isFinite(n) ? n : 0;
}

export function classifyLevel(kp: number | null, g: number, r: number, flare: string | null): SpaceWeatherLevel {
  if (g >= 1 || (kp != null && kp >= 5)) return 'storm';
  if (r >= 1 || (flare != null && /^[MX]/.test(flare)) || (kp != null && kp >= 4)) return 'active';
  return 'quiet';
}

export async function fetchSpaceWeather(): Promise<SpaceWeather | null> {
  try {
    const c = sessionStorage.getItem(CACHE_KEY);
    if (c) { const v = JSON.parse(c); if (Date.now() - v.fetchedAt < CACHE_MS) return v; }
  } catch { /* noop */ }

  try {
    const [scales, kpRows, flares] = await Promise.all([
      getJson('/products/noaa-scales.json'),
      getJson('/products/noaa-planetary-k-index.json').catch(() => null),
      getJson('/json/goes/primary/xray-flares-latest.json').catch(() => null),
    ]);
    const now = scales?.['0'] || {};
    const g = toNum(now.G?.Scale);
    const r = toNum(now.R?.Scale);

    let kp: number | null = null;
    if (Array.isArray(kpRows) && kpRows.length) {
      const last = kpRows[kpRows.length - 1];
      // 新形式：オブジェクト配列 {Kp}／旧形式：先頭行が見出しの二次元配列
      const v = Array.isArray(last) ? parseFloat(last[1]) : parseFloat(last.Kp ?? last.kp);
      kp = isFinite(v) ? Math.round(v * 10) / 10 : null;
    }

    let flareClass: string | null = null;
    if (Array.isArray(flares) && flares[0]?.max_class && flares[0]?.max_time) {
      const age = Date.now() - new Date(flares[0].max_time).getTime();
      if (age >= 0 && age < 24 * 3600 * 1000) flareClass = String(flares[0].max_class);
    }

    const v: SpaceWeather = { level: classifyLevel(kp, g, r, flareClass), kp, gScale: g, rScale: r, flareClass, fetchedAt: Date.now() };
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(v)); } catch { /* noop */ }
    return v;
  } catch {
    return null;
  }
}

/** オーロラが日本から見える可能性（目安）。北海道でも Kp 7 前後以上が必要 */
export function auroraChance(kp: number | null, lat: number | null | undefined): boolean {
  if (kp == null || lat == null) return false;
  return (lat >= 41 && kp >= 7) || (lat >= 38 && kp >= 8) || kp >= 9;
}
